import { get, pick } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { DateTime } from 'luxon'

/**
 * googleTagManagerEvent, track an event for google tag manager
 * @param {string} eventName
 */
export const googleTagManagerEvent = (event, data) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.dataLayer || !event) {
    return
  }

  window.dataLayer.push({
    event,
    ...data
  })
}

/**
 * trackTTQ: tracking for tiktok pixel
 *
 * @param {*} url
 */
export const trackTTQ = eventName => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.ttq) {
    return
  }
  window.ttq.track(eventName)
}

/**
 * segmentPageView: record a pageview
 *
 * @param {*} url
 */
export const segmentPageView = path => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.analytics) {
    return
  }
  window.analytics.page(path)
  segmentEvent('Page View', { path })
}
/**
 * segmentIdentify: identifies a user
 *
 * @param {*} id
 * @param {*} user
 */
export const segmentIdentify = (id, user) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.analytics) {
    return
  }
  
  window.analytics.identify(id, {
    external_id: id,
    ...user
  })
}

let _segmentAnonymousId
/**
 * sets and returns the current segment anon id
 *
 * @export
 * @return {*} 
 */
export function segmentAnonymousId() {
  const savedAnonymousId = localStorage.getItem('segmentAnonymousId')

  if (savedAnonymousId) {
    _segmentAnonymousId = savedAnonymousId
  } else {
    _segmentAnonymousId = uuidv4()
    localStorage.setItem('segmentAnonymousId', _segmentAnonymousId)
  }

  window?.analytics?.setAnonymousId(_segmentAnonymousId)
  return _segmentAnonymousId
}

/**
 * googleAnalyticsEvent: record an event
 *
 * @param {*} url
 */
export const googleAnalyticsEvent = (action, attributes, options) => {
  gtag('event', action, attributes, options)
}

/**
 * googleAnalyticsSet: set a value 
 * 
 */
export const googleAnalyticsSet = (action, attributes) => {
  gtag('set', action, attributes)
}

/**
 * generateSessionId - generates a session id and stores it in session storage
 *
 * @return {*} 
 */
const generateSessionId = () => {
  let sessionId = window.sessionStorage.getItem('sessionId')
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    window.sessionStorage.setItem('sessionId', sessionId)
  }
  return sessionId
}

/**   
 * segmentEvent: record an event
 *
 * @param {*} url
 */
export const segmentEvent = (action, attributes) => {
  
  try {
    if (!window.analytics) {
      return
    }

    // rokt asked us to include email in all events so grab the identified user
    const email = typeof window.analytics?.user === 'function' ? window.analytics?.user?.()?.traits?.()?.email : undefined

    window.analytics.track(action, {
      ...attributes,
      email,
      is_authenticated: !!email,
      platform: 'web',
      sessionId: generateSessionId()
    })
  } catch (error) {
    console.error(error, 'segmentEvent')
  }
}

/**
 * googleAnalyticsPageView: record a pageview
 *
 * @param {*} url
 */
export const googleAnalyticsPageView = path => {
  googleAnalyticsEvent('page_view', {
    page_path: path
  })
}

const gtag = (...args) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.gtag) {
    console.warn('gtag not found')
    return
  }
  window.gtag(...args)
}

/**
 * generateBookingEvent, parses booking info, used with trackBooking for google analytics
 * @param {*} bookedAppointment
 */

const generateBookingEvent = bookedAppointment => {
  return {
    id: bookedAppointment.id,
    revenue: bookedAppointment.appointmentServices
      .reduce(function (sum, item) {
        var price = item.service.price
        if (bookedAppointment.address.market?.tip) {
          price += price * parseFloat(bookedAppointment.address.market?.tip)
        }
        sum = sum + price
        return sum
      }, 0)
      .toFixed(2),
    tax: bookedAppointment.appointmentServices
      .reduce(function (sum, item) {
        var tax = 0
        if (bookedAppointment.address.market?.serviceTax) {
          tax =
            item.service.price *
            parseFloat(bookedAppointment.address.market?.serviceTax)
        }
        sum = sum + tax
        return sum
      }, 0)
      .toFixed(2),
    shipping: '',
    currency: 'US',
    items: bookedAppointment.appointmentServices.map(function (
      appointmentService
    ) {
      var name = appointmentService.service.name.replace(/\//g, '')
      return {
        id: bookedAppointment.id,
        price: appointmentService.service.price.toFixed(2),
        quantity: 1,
        name: name,
        sku: name.replace(/ /g, '').toUpperCase(),
        type: appointmentService.service.serviceType.name
      }
    })
  }
}

const serviceToTrackingItem = marketService => {
  var name = marketService.service.name.replace(/\//g, '')
  return {
    item_name: name,
    item_id: name.replace(/ /g, '').toUpperCase(),
    price: marketService.price.toFixed(2),
    item_brand: 'GLAMSQUAD',
    item_category: marketService.service.serviceType.name,
    quantity: 1
  }
}

export const trackViewServiceList = (market, marketServices) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: (marketServices || []).map(serviceToTrackingItem),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('view_item_list', data)
    segmentEvent('view_item_list', data)
    trackProductListViewed('All', marketServices)
  } catch (error) {
    console.warn('trackViewServiceList:', error)
  }
}

export const trackSelectService = (market, marketService) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: serviceToTrackingItem(marketService),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('select_item', data)
    segmentEvent('select_item', data)
    trackProductViewed(marketService)
  } catch (error) {
    console.warn('trackSelectService:', error)
  }
}

export const trackAddServiceToCart = (market, marketService, instances, addOnMarketServices) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: serviceToTrackingItem(marketService),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('add_to_cart', data)
    segmentEvent('add_to_cart', data)
    trackProductAdded(marketService, instances, addOnMarketServices)
    trackTTQ('AddToCart')
  } catch (error) {
    console.warn('trackAddServiceToCart:', error)
  }
}

export const trackRemoveServiceFromCart = (market, marketService, serviceRequest) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: serviceToTrackingItem(marketService),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('remove_from_cart', data)
    trackProductRemoved(marketService, serviceRequest)
  } catch (error) {
    console.warn('trackRemoveServiceFromCart:', error)
  }
}

/**
 * generatePurchaseEvent: formats event attributes for GA4 ecommerce purchase event
 *
 * @param {*} bookedAppointment
 * @returns
 */
const generatePurchaseEvent = bookedAppointment => {
  const bookingEvent = generateBookingEvent(bookedAppointment)  
  
  return {
    transaction_id: bookingEvent.id,
    value: bookingEvent.revenue,
    tax: bookingEvent.tax,
    currency: 'USD',
    items: bookedAppointment.appointmentServices.map(aps =>
      serviceToTrackingItem(aps)
    )
  }
}

/**
 * generateEventData, parses booking info, used with trackEvent for google tag manager
 * @param {*} bookedAppointment
 */

const generateEventData = (bookedAppointment, isNewUser) => {
  const serviceItems = bookedAppointment.appointmentServices.map(as => {
    return {
      sku: as.service.name,
      price: as.service.price,
      type: 'Service',
      serviceType: as.service.serviceType.name
    }
  })

  return {
    data: {
      id: bookedAppointment.id,
      price: bookedAppointment.totalPrice,
      userType: isNewUser ? 'new' : 'repeat',
      items: serviceItems,
      email: bookedAppointment.owner.email
    }
  }
}

/**
 * trackLeadGen, parses booking info to track lead gen for rakuten
 * @param {*} bookedAppointment
 */

const trackLeadGen = (
  bookedAppointment,
  transaction,
  isNewUser,
  promoCode,
  promoCodeAmount
) => {
  try {
    var rm_trans = {
      affiliateConfig: {
        ranMID: process.env.NEXT_PUBLIC_RAKUTEN_RAN_MID,
        discountType: 'item',
        includeStatus: 'false'
      },
      customerStatus: isNewUser ? 'New' : 'Existing',
      customerID: bookedAppointment.ownerId,
      discountCode: promoCode,
      discountAmount: promoCodeAmount,
      orderid: bookedAppointment.id,
      currency: 'USD',
      taxAmount: bookedAppointment.taxAmount,
      conversionType: 'Sale',
      lineitems: transaction.items.map(item => ({
        quantity: 1,
        SKU: item.sku,
        productName: item.name,
        unitPrice: item.price * 1
      }))
    }

    // prettier-ignore
    /* eslint-disable */
    /*Do not edit any information beneath this line*/
    !function (a, b, c) {
      var d = a.rakutenDataLayerName || "DataLayer"; a[d] = a[d] || {}, a[d].events = a[d].events || {}, a[d].events.SPIVersion = "3.4.1", a[d].Sale = a[d].Sale || {}, a[d].Sale.Basket = a[d].Sale.Basket || {}, c.Ready = a[d].Sale.Basket.Ready && a[d].Sale.Basket.Ready + 1 || 1, a[d].Sale.Basket = c;
      var e = function (a) { for (var c, d = a + "=", e = b.cookie.split(";"), f = 0; f < e.length; f++) { for (c = e[f]; " " == c.charAt(0);)c = c.substring(1, c.length); if (0 == c.indexOf(d)) return c.substring(d.length, c.length) } return "" }, f = function (a) { var b = a || "", c = {}; if (a || (b = e("rmStore")), b) { for (; b !== decodeURIComponent(b);)b = decodeURIComponent(b); for (var d = b.split("|"), f = 0; f < d.length; f++)c[d[f].split(":")[0]] = d[f].split(":")[1] } return c }, g = {}; g = f(); var h = function (a, b, c, d) { c = c || "", d = d || {}; var e = g[a || ""], f = d[b || ""], h = d.ignoreCookie || !1; e = h ? 0 : e; var i = e || f || c; return i = ("string" != typeof i || "false" !== i.toLowerCase()) && i, i }, k = function (a, c, d, e, f) { var g = b.createElement(a), h = -1 < b.location.protocol.indexOf("s") ? "https:" : "http:"; for (var i in c = c.replace("https:", h), g.src = c, e = e || {}, "script" == a ? e.type = e.type || "text/javascript" : (e.style = "display:none;", "img" == a && (e.alt = "", e.height = "1", e.width = "1")), e) e.hasOwnProperty(i) && g.setAttribute(i, e[i]); var j = b.getElementsByTagName(d); j = j.length ? j[0] : b.getElementsByTagName("script")[0].parentElement, f && (g.onload = f, g.onreadystatechange = function () { ("complete" == this.readyState || "loaded" == this.readyState) && f() }), j.appendChild(g) }, l = function (a) { var b = new Date, c = b.getUTCFullYear() + ("0" + (b.getUTCMonth() + 1)).slice(-2) + ("0" + b.getUTCDate()).slice(-2) + ("0" + b.getUTCHours()).slice(-2) + ("0" + b.getUTCMinutes()).slice(-2); return "NoOID_" + (a ? a + "_" : "") + Math.round(1e5 * Math.random()) + "_" + c }, m = function () { var b = a[d] && a[d].Sale && a[d].Sale.Basket ? a[d].Sale.Basket : {}, c = c || b.affiliateConfig || {}, f = h("amid", "ranMID", "", c) || b.ranMID; if (!f) return !1; var g = "undefined" == typeof c.allowCommission || "false" !== c.allowCommission; if (!g) return !1; if (!b.orderid && !(b.lineitems && b.lineitems.length)) return !1; var m = h("adn", "domain", "track.linksynergy.com", c), o = h("atm", "tagType", "pixel", c), p = h("adr", "discountType", "order", c), q = h("acs", "includeStatus", "false", c), r = h("arto", "removeOrderTax", "false", c), s = h("artp", "removeTaxFromProducts", "false", c), t = h("artd", "removeTaxFromDiscount", "false", c), u = h("atr", "taxRate", b.taxRate || 0, c), v = h("ald", "land", !1, {}) || (c.land && !0 === c.land ? e("ranLandDateTime") : c.land) || !1, w = h("atrv", "tr", !1, {}) || (c.tr && !0 === c.tr ? e("ranSiteID") : c.tr) || !1, x = h("acv", "centValues", "true", c), y = h("ancc", "nonCentCurrencies", "JPY", c); u = Math.abs(+u); var z = (100 + u) / 100, A = b.orderid || l(f); A = encodeURIComponent(A); var B = b.currency || ""; B = encodeURIComponent(B.toUpperCase()); var C = !1; if (B && y) { y = (y + "").split(","); for (var D = 0; D < y.length; D++)y[D] == B && (C = !0) } var F = function (a) { return C && (a = Math.round(a)), x && "false" !== x ? (a *= 100, a = Math.round(a)) : a = Math.round(100 * a) / 100, a + "" }, G = b.taxAmount ? Math.abs(+b.taxAmount) : 0, H = b.discountAmount ? Math.abs(+b.discountAmount) : 0, I = b.discountAmountLessTax ? Math.abs(+b.discountAmountLessTax) : 0; !I && H && t && u && (I = H / z), I = I || H; var J = "ep"; "mop" === o && (J = "eventnvppixel"); var K = (b.customerStatus || "") + "", L = ""; K && (q && "EXISTING" == K.toUpperCase() || q && "RETURNING" == K.toUpperCase()) && (L = "R_"); for (var M = [], N = 0, O = 0; O < (b.lineitems ? b.lineitems.length : 0); O++)if (b.lineitems[O]) { var P = !1, Q = a.JSON ? JSON.parse(JSON.stringify(b.lineitems[O])) : b.lineitems[O], R = +Q.quantity || 0, S = +Q.unitPrice || 0, T = +Q.unitPriceLessTax, U = T || S || 0; s && u && !T && (U /= z); for (var V, W = R * U, X = 0; X < M.length; X++)V = M[X], V.SKU === Q.SKU && (P = !0, V.quantity += R, V.totalValue += W); P || (Q.quantity = R, Q.totalValue = W, M.push(Q)), N += W } for (var Y = "", Z = "", $ = "", _ = "", aa = {}, O = 0; O < M.length; O++) { var Q = M[O], ba = encodeURIComponent(Q.SKU), ca = Q.totalValue, R = Q.quantity, da = encodeURIComponent(Q.productName) || ""; "item" === p.toLowerCase() && I && (ca -= I * ca / N); var ea = Q.optionalData; for (var fa in ea) ea.hasOwnProperty(fa) && (aa[fa] = aa[fa] || "", aa[fa] += encodeURIComponent(ea[fa]) + "|"); Y += L + ba + "|", Z += R + "|", $ += F(ca) + "|", _ += L + da + "|" } Y = Y.slice(0, -1), Z = Z.slice(0, -1), $ = $.slice(0, -1), _ = _.slice(0, -1), I && (I = F(I)), G && (G = F(G)), I && "order" === p.toLowerCase() && (Y += "|" + L + "DISCOUNT", _ += "|" + L + "DISCOUNT", Z += "|0", $ += "|-" + I), r && G && (Y += "|" + L + "ORDERTAX", Z += "|0", $ += "|-" + G, _ += "|" + L + "ORDERTAX"); var ga = "https://" + m + "/" + J + "?mid=" + f; ga += "&ord=" + A, ga += v ? "&land=" + v : "", ga += w ? "&tr=" + w : "", ga += "&cur=" + B, ga += "&skulist=" + Y, ga += "&qlist=" + Z, ga += "&amtlist=" + $, ga += "&img=1", ga += "&spi=" + a[d].events.SPIVersion, I && "item" === p.toLowerCase() && (ga += "&discount=" + I); var ea = b.optionalData || {}; for (var fa in b.discountCode && (ea.coupon = b.discountCode), b.customerStatus && (ea.custstatus = b.customerStatus), b.customerID && (ea.custid = b.customerID), I && (ea.disamt = I), ea) ea.hasOwnProperty(fa) && (ga += "&" + encodeURIComponent(fa) + "=" + encodeURIComponent(ea[fa])); for (var fa in aa) aa.hasOwnProperty(fa) && (ga += "&" + encodeURIComponent(fa) + "list=" + aa[fa].slice(0, -1), I && "order" === p.toLowerCase() && (ga += "|"), G && r && (ga += "|")); ga += "&namelist=" + _; if (8000 < ga.length) { for (var ha = 8000; 0 < ha;)if ("&" == ga.charAt(ha)) { ga = ga.slice(0, ha); break } else ha--; ga += "&trunc=true" } k("img", ga, "body") }, n = function () { var b = a[d] && a[d].Sale && a[d].Sale.Basket ? a[d].Sale.Basket : {}, c = c || b.displayConfig || {}, e = h("dmid", "rdMID", "", c); if (!e) return !1; if (!b.orderid && !b.conversionType) return !1; var f = h("dtm", "tagType", "js", c), g = h("ddn", "domain", "tags.rd.linksynergy.com", c), j = h("dis", "includeStatus", "false", c), m = h("dcomm", "allowCommission", "notset", c), n = h("duup", "useUnitPrice", "false", c), o = h("drtp", "removeTaxFromProducts", "false", c), p = h("drtd", "removeTaxFromDiscount", "false", c), q = h("dtr", "taxRate", b.taxRate || 0, c), r = ""; "true" === m || !0 === m || "1" === m || 1 === m ? r = "1" : ("false" === m || !1 === m || "0" === m || 0 === m) && (r = "0"), f = "js" === f || "if" === f || "img" === f ? f : "js"; var s = "script"; "if" === f ? s = "iframe" : "img" === f && (s = "img"), ("true" === n || !0 === n || "1" === n || 1 === n) && (n = !0); var t = (b.customerStatus || "") + "", u = ""; t && j && ("EXISTING" == t.toUpperCase() || "RETURNING" == t.toUpperCase()) && (u = "R_"); var v = b.orderid; v || (v = l((b.conversionType + "").toLowerCase() + "_" + e)), v = encodeURIComponent(u + v); var w = encodeURIComponent(b.currency || ""), x = 0, y = ""; q = Math.abs(+q); var z = (100 + q) / 100, A = b.discountAmount ? Math.abs(+b.discountAmount) : 0, B = b.discountAmountLessTax ? Math.abs(+b.discountAmountLessTax) : 0; !B && A && p && q && (B = A / z), B = B || A, B = isNaN(B) ? 0 : B; for (var C = 0; C < (b.lineitems ? b.lineitems.length : 0); C++)if (b.lineitems[C]) { var D = +b.lineitems[C].quantity, E = +b.lineitems[C].unitPriceLessTax * D; (!E || n) && (o && q ? E = +b.lineitems[C].unitPrice / z * D : E = +b.lineitems[C].unitPrice * D), E = isNaN(E) ? 0 : E, x += E, y += encodeURIComponent(b.lineitems[C].SKU) + "," } x = Math.round(100 * (x - B)) / 100, y = y.slice(0, -1); var F = "https://" + g + "/" + f + "/" + e; F += "/?pt=" + "conv", F += "&orderNumber=" + v, F += "&spi=" + a[d].events.SPIVersion, x && (F += "&price=" + x), w && (F += "&cur=" + w), r && (F += "&tvalid=" + r), y && (F += "&prodID=" + y), k(s, F, "body") }, o = function () { var b = a[d] && a[d].Sale && a[d].Sale.Basket ? a[d].Sale.Basket : {}, c = b.searchConfig || {}, e = 1024, f = encodeURIComponent("...TRUNCATED"), g = h("smid", "rsMID", "", c); if (!g) return !1; var j = h("said", "accountID", "113", c), m = h("sclid", "clickID", "", c), n = encodeURIComponent(h("sct", "conversionType", b.conversionType && "sale" !== (b.conversionType + "").toLowerCase() ? b.conversionType : "conv", c)); k("script", "https://services.xg4ken.com/js/kenshoo.js?cid=" + g, "body", null, function () { var a = {}; if (a.conversionType = n, a.revenue = 0, a.currency = encodeURIComponent(b.currency || "USD"), a.orderId = encodeURIComponent(b.orderid || l(n)), a.promoCode = encodeURIComponent(b.discountCode || ""), m && (a.ken_gclid = encodeURIComponent(m)), a.discountAmount = +(b.discountAmount || 0), a.discountAmount = isNaN(a.discountAmount) ? 0 : Math.abs(a.discountAmount), a.customerStatus = encodeURIComponent(b.customerStatus || ""), a.productIDList = "", a.productNameList = "", b.lineitems && b.lineitems.length) { for (var c = 0; c < b.lineitems.length; c++)b.lineitems[c] && (a.revenue += +(b.lineitems[c].unitPrice || 0) * +b.lineitems[c].quantity, a.productIDList += (b.lineitems[c].SKU || "NA") + ",", a.productNameList += (b.lineitems[c].productName || "NA") + ","); a.revenue = Math.round(100 * (a.revenue - a.discountAmount)) / 100, a.productIDList = encodeURIComponent(a.productIDList.slice(0, -1)), a.productNameList = encodeURIComponent(a.productNameList.slice(0, -1)), a.productIDList.length > e && (a.productIDList = a.productIDList.substring(0, e - f.length) + f), a.productNameList.length > e && (a.productNameList = a.productNameList.substring(0, e - f.length) + f) } kenshoo.trackConversion(j, g, a) }) }; a[d].SPI = { readRMCookie: e, processRMStoreCookie: f, readRMStoreValue: h, sRAN: m, sDisplay: n, sSearch: o, addElement: k, rmStore: g }, m(), n(), o()
    }(window, document, rm_trans);
    /* eslint-enable */

  } catch (error) {
    console.warn('trackLeadGen:', error)
  }
}

/**
 *  trackBooking, track a new booking for google analytics, google tag manager, and rakuten.
 * @param {*} bookedAppointment
 */
export const trackBooking = (
  bookedAppointment,
  isNewUser,
  promoCode,
  promoCodeAmount,
  marketServicesForMarket = [],
  serviceRequests = [],
  market,
  user,
) => {
  try {
    // GTM
    const data = generateEventData(bookedAppointment, isNewUser)
    googleTagManagerEvent('book', data)
    trackTTQ('CompletePayment')

    // send GA4 purchase event
    const purchaseEventData = {
      ...generatePurchaseEvent(bookedAppointment),
      market: market?.shortName,
      ...pick(user, ['email', 'nameFirst', 'nameLast', 'phone']),
      ...pick(bookedAppointment?.address, ['city', 'state', 'zip']),
    }

    googleAnalyticsEvent('purchase', purchaseEventData)
    
    googleAnalyticsEvent(`purchase_${get(data, 'data.userType')}`, purchaseEventData)

    // alternate ecommerce event (used by GTM/Facebook integration?)
    const transaction = generateBookingEvent(bookedAppointment)

    gtag('require', 'ecommerce')

    googleAnalyticsEvent('ecommerce:addTransaction', {
      id: transaction.id,
      revenue: transaction.revenue,
      tax: transaction.tax
    })

    transaction.items.forEach(function (item) {
      googleAnalyticsEvent('ecommerce:addItem', {
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        sku: item.sku
      })
    })

    googleAnalyticsEvent('ecommerce:send')

    // RAKUTEN
    // if (process.env.NODE_ENV === 'production') {
    //   trackLeadGen(bookedAppointment);
    // }

    trackLeadGen(
      bookedAppointment,
      transaction,
      isNewUser,
      promoCode,
      promoCodeAmount
    )

    const hoursInAdvance = Math.floor(DateTime.fromSeconds(bookedAppointment.actualStartDateTime).diffNow('hours').hours)

    trackOrderCompleted({ ...bookedAppointment, hoursInAdvance, isNewUser }, marketServicesForMarket, serviceRequests)
  } catch (error) {
    console.warn('trackBooking:', error)
  }
}

/**
 * sends a Lead event to facebook w/ content_category
 *
 * @param {*} contentCategory
 */
export const trackFacebookLead = () /* contentCategory */ => {
  // try {
  //   if (typeof window === 'undefined') {
  //     return
  //   }
  //   const { default: ReactPixel } = require('react-facebook-pixel');
  //   if (!ReactPixel?.track) {
  //     return
  //   }
  //   ReactPixel.track('Lead', { content_category: contentCategory })
  // } catch (error) {
  //   console.warn('trackFacebookLead:', error)
  // }
}

export const trackCheckout = data => {
  try {
    segmentEvent('checkout', data)
    googleAnalyticsEvent('checkout', data)
    trackTTQ('CompletePayment')
    trackOrderCompleted(data)
  } catch (error) {
    console.warn('trackCheckout:', error)
  }

}

/**
  * object containing currently used standard events 
  * that segment supports 
  * https://segment.com/docs/connections/spec/b2b-saas
  * https://segment.com/docs/connections/spec/ecommerce/v2/#event-lifecycles
  */
export const segmentStandardEvents = {
  PRODUCT_LIST_VIEWED: 'Product List Viewed',
  PRODUCT_CLICKED: 'Product Clicked',
  PRODUCT_VIEWED: 'Product Viewed',
  PRODUCT_ADDED: 'Product Added',
  PRODUCT_REMOVED: 'Product Removed',
  CHECKOUT_STARTED: 'Checkout Started',
  ORDER_COMPLETED: 'Order Completed',
  PAYMENT_INFO_ENTERED: 'Payment Info Entered',
  AVAILABILITY_SEARCH: 'Availability Search',
  SIGNED_UP: 'Signed Up',
  SIGNED_IN: 'Signed In'
}

export const trackOrderCompleted = (order, marketServicesForMarket, serviceRequests) => {
  try {
    if (!order) {
      return
    }

    let data

    // no appointment services meaning its a package, membership, or gift card
    if (!order.appointmentServices) {
      const { item } = order
      data = {
        checkout_id: order.transaction_id,
        subtotal: item.price,
        revenue: item.price,
        total: item.price,
        tax: order.tax,
        tip: order.tip,
        discount: order.promo,
        currency: order.currency,
        type: item.isMembership ? 'membership' : 'package',
        products: formatSegmentProduct(order.item),
        isNewUser: order.isNewUser
      }
    }

    // format data for tracking an appointment
    if (order.appointmentServices) {
      const {
        id,
        totalPrice,
        handlingFee,
        taxAmount,
        tipAmount,
        creditsApplied,
        startDateTime,
        isNewUser,
        address
      } = order

      const _startDateTime = DateTime.fromSeconds(startDateTime, {
        zone: address.market?.timezone
      })

      data = {
        checkout_id: id,
        subtotal: totalPrice,
        revenue: totalPrice,
        value: totalPrice,
        total: totalPrice,
        tax: taxAmount,
        tip: tipAmount,
        fee: handlingFee,
        discount: creditsApplied,
        isNewUser,
        type: 'appointment',
        startDateTime: _startDateTime.toISO(),
        dayOfWeek: _startDateTime.weekdayLong,
        currency: 'USD',
        market: address.market?.shortName
      }

      const products = serviceRequests.flatMap(serviceRequest => formatServiceRequestAsSegmentProduct(serviceRequest, marketServicesForMarket))

      if (products) {
        data.products = products
      }
    }
        
    segmentEvent(segmentStandardEvents.PAYMENT_INFO_ENTERED, data)

    segmentEvent(segmentStandardEvents.ORDER_COMPLETED, data)

    if (order.appointmentServices) {
      segmentEvent(`purchase_${order.isNewUser ? 'new' : 'repeat'}`, data)
    }

  } catch (error) {
    console.warn('trackOrderCompleted:', error)
  }
}

/**
  * called in OrderSummary and step 5 of booking flow
  * handles tracking appointment objects or package orderItems
  * formats products and attaches to data object
  * tracks the checkout started screen with data 
  * marketServices only passed when coming from booking flow
  */
export const trackCheckoutStarted = (checkoutInfo, marketServices = [], serviceRequests = []) => {
  try {
    if (!checkoutInfo) {
      return
    }

    const data = {
      value: checkoutInfo.finalPrice,
      revenue: checkoutInfo.totalPrice,
      total: checkoutInfo.totalPrice,
      tax: checkoutInfo.taxAmount,
      tip: checkoutInfo.tipAmount,
      fee: checkoutInfo.handlingFee,
      discount: checkoutInfo.creditsApplied || 0,
      currency: 'USD',
    }

    if (checkoutInfo.actualStartDateTime) {
      data.hoursInAdvance = Math.floor(DateTime.fromSeconds(checkoutInfo.actualStartDateTime).diffNow('hours').hours);
    }

    // when tracking appointment booking 
    if (checkoutInfo.appointmentServices) {

      const serviceNames = []
      const serviceTypes = []
      checkoutInfo.appointmentServices.forEach(appointmentService => {
        const { service: { serviceType } } = marketServices.find(ms => ms.service.id === appointmentService.service.id)

        // attach service type to appintment service's service for category tracking
        appointmentService.service.serviceType = serviceType
        serviceNames.push(appointmentService.service.name)
        serviceTypes.push(serviceType.name)
      })

      data.products = serviceRequests.flatMap(serviceRequest => formatServiceRequestAsSegmentProduct(serviceRequest, marketServices))

      data.market = checkoutInfo.address.market?.shortName
      data.type = 'Appointment'
      data.serviceNames = serviceNames.join(', ')
      data.serviceTypes = serviceTypes.join(', ')

      const startDateTime = DateTime.fromSeconds(checkoutInfo.startDateTime, {
        zone: checkoutInfo.address.market?.timezone
      })

      data.startDateTime = startDateTime.toISO()
      data.dayOfWeek = startDateTime.weekdayLong
    }

    // tracking anything but an appointment orderItem will be present
    if (checkoutInfo.orderItem) {
      data.products = formatSegmentProduct(checkoutInfo.orderItem)
      data.type = checkoutInfo.orderItem.isMembership ? 'Membership' : 'Package'
      data.isNewUser = checkoutInfo.isNewUser
    }

    segmentEvent(segmentStandardEvents.CHECKOUT_STARTED, data)
  } catch (error) {
    console.warn('trackCheckoutStarted:', error)
  }
}

// tracking for viewing full product lists (packages, services)
export const trackProductListViewed = (category, items) => {
  try {
    if (!(category && items)) {
      return
    }
    segmentEvent(segmentStandardEvents.PRODUCT_LIST_VIEWED, {
      category,
      products: formatSegmentProduct(items)
    })
  } catch (error) {
    console.warn('trackProductListViewed:', error)
  }
}

// tracking for viewing a product details
export const trackProductViewed = product => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_VIEWED, formatSegmentProduct(product)[0])
  } catch (error) {
    console.warn('trackProductViewed:', error)
  }
}

// tracking for membership join buttons
export const trackProductClicked = product => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_CLICKED, formatSegmentProduct(product)[0])
  } catch (error) {
    console.warn('trackProductClicked:', error)
  }
}

// tracking for adding product to cart
export const trackProductAdded = (product, instances, addOnMarketServices) => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_ADDED, formatSegmentProduct({ ...product, instances, addOnMarketServices })[0])
  } catch (error) {
    console.warn('trackProductAdded:', error)
  }
}

//tracking for removing a product from cart
export const trackProductRemoved = (marketService, serviceRequest) => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_REMOVED, formatServiceRequestAsSegmentProduct(serviceRequest, [marketService])[0])
  } catch (error) {
    console.warn('trackProductRemoved:', error)
  }
}

export const trackPaymentInfoEntered = () => {
  try {
    segmentEvent(segmentStandardEvents.PAYMENT_INFO_ENTERED)
  } catch (error) {
    console.warn('trackPaymentInfoEntered:', error)
  }
}


/**
 * formatServiceRequestAsSegmentProduct - formats a service request for segment
 *
 * @param {*} serviceRequest
 * @param {*} marketServices
 * @return {*} 
 */
const formatServiceRequestAsSegmentProduct = (serviceRequest, marketServices) => {
  if (!(serviceRequest && marketServices)) {
    return {}
  }
  const marketService = marketServices.find(ms => ms.service.id === serviceRequest.serviceId)
  const addOnMarketServices = marketService.addOnMarketServices?.filter(addOnMarketService => serviceRequest.addOnServiceIds.includes(addOnMarketService.service.id))
  const price = marketService.price + (addOnMarketServices?.reduce((total, addOnMarketService) => total + addOnMarketService.addOnPrice, 0) || 0)
  return formatSegmentProduct({ ...serviceRequest, ...marketService, addOnMarketServices, price })
}

/**
 * formatSegmentProduct - formats a product for segment
 *
 * @param {*} products
 * @return {*} 
 */
export const formatSegmentProduct = products => {
  try {
    if (!products) {
      return []
    }

    return (Array.isArray(products) ? products : [products]).map(product => ({
      product_id: product.service ? product.service.id : product.id,
      name: product.service ? product.service.name : product.name,
      price: product.price,
      category: product.service ? product.service.serviceType.name : product.isMembership ? 'Membership' : 'Package',
      quantity: product.instances || 1,
      variant: product.addOnMarketServices?.map(addOnMarketService => addOnMarketService.service.addOnName).join(', '),
      add_ons: product.addOnMarketServices?.map(addOnMarketService => addOnMarketService.service.addOnName),
    }))
  } catch (error) {
    return [];
  }
}

/**
 * trackError - tracks an error to segment
 *
 * @param {*} title
 * @param {*} error
 */
export const trackError = (title, error) => {
  if (!title || !error) {
    return
  }

  segmentEvent('Error', {
    title,
    message: error.message,
    error,
  })
}

// Availability Search Tracking Events 
const segmentCustomEvents = {
  AVAILABILITY_SEARCH_RESULT_FOUND: 'Availability Search Result Found',
  AVAILABILITY_SEARCH_RESULT_NOT_FOUND: 'Availability Search Result Not Found',
  AVAILABILITY_SEARCH_RESULT_ALTERNATE_TIME_FOUND: 'Availability Search Result Alternate Time Found',
  AVAILABILITY_SEARCH_RESULT_WITHOUT_ADDONS_FOUND: 'Availability Search Result Without Addons Found',
  AVAILABILITY_SEARCH_RESULT_WITHOUT_SERVICE_FOUND: 'Availability Search Result Without Service Found',
  AVAILABILITY_SEARCH_RESULT_ALTERNATE_SERVICE_FOUND: 'Availability Search Result Alternate Service Found',
}

// tracking for step 3 of booking flow: tracks market services from service requests
const trackAvailabilitySearchSegment = (eventName, startDateTime, serviceRequests, marketServices) => {
  try {
    if (!(startDateTime && serviceRequests && marketServices && marketServices.length)) {
      return
    }

    const products = serviceRequests.flatMap(serviceRequest => formatServiceRequestAsSegmentProduct(serviceRequest, marketServices))

    startDateTime = startDateTime.toISO ? startDateTime : DateTime.fromJSDate(startDateTime)

    segmentEvent(eventName, {
      startDateTime: startDateTime.toISO(),
      hoursInAdvance: Math.floor(startDateTime.diffNow('hours').hours),
      products,
    })
  } catch (error) {
    console.warn('trackAvailabilitySearchSegment:', error)
  }
}

export const trackAvailabilitySearch = (startDateTime, serviceRequests, marketServices) => {
  trackAvailabilitySearchSegment(segmentStandardEvents.AVAILABILITY_SEARCH, startDateTime, serviceRequests, marketServices)
}

export const trackAvailabilitySearchResultFound = (startDateTime, serviceRequests, marketServices) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_FOUND, startDateTime, serviceRequests, marketServices)
}

export const trackAvailabilitySearchResultNotFound = (startDateTime, serviceRequests, marketServices) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_NOT_FOUND, startDateTime, serviceRequests, marketServices)
}

export const trackAvailabilitySearchResultNoAddonsFound = (startDateTime, serviceRequests, marketServices) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_WITHOUT_ADDONS_FOUND, startDateTime, serviceRequests, marketServices)
}

export const trackAvailabilitySearchAlternateTimeFound = (startDateTime, serviceRequests, marketServices) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_ALTERNATE_TIME_FOUND, startDateTime, serviceRequests, marketServices)
}

export const trackAvailabilitySearchWithoutServiceFound = (startDateTime, serviceRequests, marketServices) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_WITHOUT_SERVICE_FOUND, startDateTime, serviceRequests, marketServices)
}

export const trackAvailabilitySearchAlternateServiceFound = (startDateTime, serviceRequests, marketServices) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_ALTERNATE_SERVICE_FOUND, startDateTime, serviceRequests, marketServices)
}

export const trackSignedIn = (email, id) => {
  try {
    segmentEvent(segmentStandardEvents.SIGNED_IN, {
      properties: {
        username: email
      },
      context: {
        groupId: id
      }
    })
  } catch (error) {
    console.warn('trackSignedIn:', error)
  }
}

export const trackSignedUp = (properties, id) => {
  try {
    segmentEvent(segmentStandardEvents.SIGNED_UP, {
      properties,
      context: {
        groupId: id
      }
    })
  } catch (error) {
    console.warn('trackSignedIn:', error)
  }
}
